import { PromoCampaign, PromotionPreview, RedeemPromotionResult } from '../types';
import { attachAuthInterceptor } from './authToken';
import axios from 'axios';

const BASE_URL = (process.env.REACT_APP_CLUBHOUSE_API_URL || 'http://localhost:5000').replace(/\/+$/, '');

const apiClient = axios.create({
  baseURL: `${BASE_URL}/api`,
});
attachAuthInterceptor(apiClient);

/**
 * Redemption of promo codes. A golfer opens a redeem link (or enters a code), previews the
 * promotion, then redeems it for their signed-in account — consuming the single-use code and
 * adding its Caddie Credits to their balance. Also reads promo campaigns: the shared links and
 * QR codes that give a new golfer a free trial (started when they finish onboarding).
 */
export const promotionApi = {
  /** Previews a promo code (name, message, credits, whether it's used/expired), or `null` when unknown. */
  preview: async (code: string): Promise<PromotionPreview | null> => {
    try {
      const { data } = await apiClient.get<PromotionPreview>(`/promotions/${encodeURIComponent(code)}`);
      return data;
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) return null;
      throw err;
    }
  },

  /** Redeems a promo code for the signed-in golfer, adding its Caddie Credits. */
  redeem: async (code: string, userId: string, email?: string | null): Promise<RedeemPromotionResult> => {
    try {
      const { data } = await apiClient.post<RedeemPromotionResult>(
        `/promotions/${encodeURIComponent(code)}`,
        { userId, email: email ?? null },
      );
      return data;
    } catch (err) {
      // The API returns 400 with a RedeemPromotionResult body for invalid/used/expired codes.
      if (axios.isAxiosError(err) && err.response?.status === 400 && err.response.data) {
        return err.response.data as RedeemPromotionResult;
      }
      throw err;
    }
  },

  /**
   * Describes a promo campaign (the offer behind a shared link or QR code) for the banner and the
   * landing page, or `null` when no such campaign exists. Works before sign-in. An ended campaign
   * still comes back, with `active` false.
   */
  getCampaign: async (slug: string): Promise<PromoCampaign | null> => {
    try {
      const { data } = await apiClient.get<PromoCampaign>(`/promotions/campaigns/${encodeURIComponent(slug)}`);
      return data;
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 404) return null;
      throw err;
    }
  },

  /**
   * Records on the signed-in golfer's account that they arrived through a promo campaign, so the
   * offer survives finishing sign-up in another browser (the confirmation email may open there).
   * Rejected with 400 when the offer has ended or the golfer already has a plan.
   */
  claimCampaign: async (slug: string): Promise<void> => {
    await apiClient.post(`/promotions/campaigns/${encodeURIComponent(slug)}/claim`);
  },
};
